import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap, finalize, shareReplay } from 'rxjs/operators';
import { UploadedFile } from '../models';
import { API_BASE } from '../api.config';
import { JobService } from './job.service';
import { TopsService } from './tops.service';

/** Backend response from POST /api/upload. */
export interface UploadResponse {
  gr: { fileCount: number; rowCount: number; columns: string[] };
  tops: { source: string; rowCount: number; columns: string[] };
}

/**
 * Stores the user-uploaded GR (LAS) files and exposes them as shared state for
 * the rest of the pipeline. A lightweight parse is done in the browser for the
 * preview cards; the raw files are sent to the FastAPI backend, which runs the
 * actual pipeline. Formation tops are fetched from Azure SQL server-side, so no
 * tops file is uploaded. GR logs are LAS files and any number may be added.
 */
@Injectable({ providedIn: 'root' })
export class DataUploadService {
  private readonly http = inject(HttpClient);
  private readonly jobs = inject(JobService);
  private readonly tops = inject(TopsService);
  private uploaded = false;
  /** The single in-flight upload, shared so concurrent callers reuse one job. */
  private inFlight: Observable<void> | null = null;

  /** Parsed metadata for every uploaded GR LAS file. */
  readonly grFiles = signal<UploadedFile[]>([]);

  /** Raw GR LAS File objects, kept for later upload to the backend. */
  private grRaw: File[] = [];

  /** True once at least one GR LAS file is present. */
  readonly ready = computed(() => this.grFiles().length > 0);

  /**
   * Send the raw GR LAS files to the backend, which loads them into the
   * pipeline session. Must succeed before the discovery/interval steps work.
   */
  uploadToBackend(): Observable<UploadResponse> {
    if (!this.grRaw.length) {
      throw new Error('At least one LAS file is required before upload.');
    }
    const form = new FormData();
    // Repeated 'gr' fields -> FastAPI receives a list[UploadFile].
    this.grRaw.forEach((f) => form.append('gr', f, f.name));
    return this.http.post<UploadResponse>(`${API_BASE}/upload`, form);
  }

  /**
   * Upload to the backend exactly once. Returns immediately if the current
   * files were already uploaded this session. Used by the route guard so the
   * backend session always exists before discovery/interval/etc.
   *
   * Uses the job flow: create job -> browser uploads DIRECTLY to Blob (no API
   * gateway 230s limit) -> backend parses -> poll until ready.
   */
  ensureUploaded(): Observable<void> {
    if (this.uploaded) {
      return of(void 0);
    }
    // If an upload is already running (e.g. the Continue button AND the route
    // guard both ask at once), reuse it instead of creating a second job.
    if (this.inFlight) {
      return this.inFlight;
    }
    if (!this.grRaw.length) {
      throw new Error('At least one LAS file is required before upload.');
    }
    this.inFlight = this.jobs.run(this.grRaw, this.tops.selectedLocation()).pipe(
      tap(() => (this.uploaded = true)),
      map(() => void 0),
      finalize(() => (this.inFlight = null)),
      shareReplay(1),
    );
    return this.inFlight;
  }

  /** Parse a file header (LAS only) and store it under the GR kind.
   *  GR LAS files are appended. */
  async addFile(kind: 'gr', file: File): Promise<UploadedFile> {
    const columns: string[] = [];
    const rowCount = 0;

    // LAS (.las) GR files are parsed server-side by the backend, so no
    // client-side column/row extraction here.

    const parsed: UploadedFile = {
      kind,
      fileName: file.name,
      size: file.size,
      columns,
      rowCount,
    };

    this.grRaw.push(file);
    this.grFiles.update((list) => [...list, parsed]);
    this.uploaded = false; // new file → force a fresh backend upload
    return parsed;
  }

  /** Remove a single GR LAS file by index. */
  removeGrAt(index: number): void {
    this.grRaw.splice(index, 1);
    this.grFiles.update((list) => list.filter((_, i) => i !== index));
    this.uploaded = false;
  }

  remove(kind: 'gr'): void {
    this.grRaw = [];
    this.grFiles.set([]);
    this.uploaded = false; // files changed → require re-upload
  }

  /**
   * Force a fresh backend upload the next time ``ensureUploaded`` is called,
   * without discarding the staged files. Used when the tops Location changes so
   * the next run re-fetches tops scoped to the newly-selected location.
   */
  invalidate(): void {
    this.uploaded = false;
  }
}